import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { Moon, Sun, Menu, User, ArrowRight, LogOut, Star, Heart, Mail, Users, Briefcase, BarChart3, Database, ChevronDown, Building2, Settings, UserCircle } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { NotificationCenter } from '@/components/notifications';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectSeparator } from '@/components/ui/select';
import { useAccountSettings } from '@/context/AccountSettingsContext';
import { useBusinessSettings } from '@/context/BusinessSettingsContext';

interface NavbarProps {
  businesses?: Array<{id: string, name: string, favicon_url?: string | null}>;
  selectedBusinessId?: string | null;
  onBusinessChange?: (businessId: string) => void;
  loadingBusinesses?: boolean;
  hideBusinessNavigation?: boolean;
  enableScrollHide?: boolean;
  isPortfolioView?: boolean;
}

type ExpandedSection = 'features' | 'pricing' | null;

const Navbar: React.FC<NavbarProps> = ({
  businesses = [],
  selectedBusinessId = null,
  onBusinessChange,
  loadingBusinesses = false,
  hideBusinessNavigation = false,
  enableScrollHide = false,
  isPortfolioView = false
}) => {
  const { user, isEmployee } = useAuth();
  const { isAccountSettingsOpen, openAccountSettings, closeAccountSettings } = useAccountSettings();
  const { isBusinessSettingsOpen, openBusinessSettings, closeBusinessSettings } = useBusinessSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const isMobile = useIsMobile();
  const [expandedSection, setExpandedSection] = useState<ExpandedSection>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Scroll-hide behavior state
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const lastScrollY = useRef(0);


  // Scroll-hide effect: hide on scroll down, show on scroll up
  useEffect(() => {
    if (!enableScrollHide) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollThreshold = 10;

      // Always show when near top of page
      if (currentScrollY < 100) {
        setIsNavbarVisible(true);
      } else if (currentScrollY > lastScrollY.current + scrollThreshold) {
        // Scrolling down - hide navbar
        setIsNavbarVisible(false);
      } else if (currentScrollY < lastScrollY.current - scrollThreshold) {
        // Scrolling up - show navbar
        setIsNavbarVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [enableScrollHide]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success("Logged out successfully!", { category: "Authentication" });
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to log out", { category: "Authentication" });
    }
  };

  const handleAccountSettingsClick = () => {
    // Toggle account settings - close business settings if open
    if (isAccountSettingsOpen) {
      closeAccountSettings();
    } else {
      closeBusinessSettings(); // Close business settings first
      openAccountSettings();
    }
  };

  const handleBusinessSettingsClick = () => {
    // Toggle business settings - close account settings if open
    if (isBusinessSettingsOpen) {
      closeBusinessSettings();
    } else {
      closeAccountSettings(); // Close account settings first
      openBusinessSettings();
    }
  };

  const isAccountSettingsActive = () => {
    return isAccountSettingsOpen;
  };

  const isBusinessSettingsActive = () => {
    return isBusinessSettingsOpen;
  };

  // Determine if we're on a dashboard page where business settings should be available
  const isOnDashboardPage = location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/employee-dashboard') ||
    location.pathname.startsWith('/portfolio');

  // Show business settings button when: user is logged in, on dashboard, and has a selected business
  const showBusinessSettingsButton = user && isOnDashboardPage && selectedBusinessId;

  const handleBusinessChange = (businessId: string) => {
    if (businessId === 'manage-businesses') {
      // Open account settings to plan management
      handleAccountSettingsClick();
      return;
    }

    if (onBusinessChange) {
      onBusinessChange(businessId);
    }
    // Toast is shown by BusinessContext.switchBusiness
  };

  const handleSectionMouseEnter = (section: ExpandedSection) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // If switching between sections, close first then open new section
    if (expandedSection && section && expandedSection !== section) {
      setExpandedSection(null);
      setTimeout(() => {
        setExpandedSection(section);
      }, 350);
    } else {
      setExpandedSection(section);
    }
  };

  const handleSectionMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setExpandedSection(null);
    }, 200);
  };

  const showBusinessSelect = user && businesses.length > 0 && !hideBusinessNavigation && location.pathname !== '/' && location.pathname !== '/features' && location.pathname !== '/pricing';

  return (
    <>
      {/* Spacer to prevent content jump when navbar is fixed */}
      {enableScrollHide && <div className="h-12" />}
      <nav className={`${enableScrollHide ? 'fixed' : 'sticky'} top-0 left-0 right-0 h-12 z-50 bg-background shadow-sm border-b border-border transition-transform duration-300 ${
        enableScrollHide && !isNavbarVisible ? '-translate-y-full' : 'translate-y-0'
      }`}>
      <div className={`mx-auto flex items-center h-12 ${isMobile ? 'justify-between px-2' : 'justify-between px-2'}`}>
        {/* Left section */}
        <div className={`flex items-center min-w-0 ${isMobile ? 'flex-1' : 'gap-2 flex-1'}`}>
          {isMobile && user && !hideBusinessNavigation && (
            <SidebarTrigger className="border border-input" />
          )}

          {/* Desktop: Business select on left */}
          {!isMobile && (
            showBusinessSelect ? (
              <Select
                value={selectedBusinessId || ""}
                onValueChange={handleBusinessChange}
                disabled={loadingBusinesses}
              >
                <SelectTrigger className="min-w-[120px] max-w-[200px] w-auto shrink h-8 px-2 text-xs [&>span]:truncate">
                  {(() => {
                    const selectedBusiness = businesses.find(b => b.id === selectedBusinessId);
                    return selectedBusiness?.favicon_url ? (
                      <div className="h-5 w-5 mr-1.5 shrink-0 rounded-full bg-white flex items-center justify-center ring-1 ring-border">
                        <img
                          src={selectedBusiness.favicon_url}
                          alt=""
                          className="h-3.5 w-3.5 rounded-sm"
                          onError={(e) => {
                            const container = e.currentTarget.parentElement;
                            if (container) container.style.display = 'none';
                            container?.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      </div>
                    ) : null;
                  })()}
                  <Building2 className={`h-3.5 w-3.5 mr-1.5 shrink-0 ${businesses.find(b => b.id === selectedBusinessId)?.favicon_url ? 'hidden' : ''}`} />
                  <SelectValue placeholder="Select Business" />
                </SelectTrigger>
                <SelectContent>
                  {businesses.map((business) => (
                    <SelectItem key={business.id} value={business.id} hideIndicator>
                      {business.name}
                    </SelectItem>
                  ))}
                  <SelectSeparator />
                  <SelectItem value="manage-businesses" className="text-primary font-medium" hideIndicator>
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      <span>Manage Businesses</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Link to="/" className="flex-shrink-0 flex items-center">
                <img src="/media/tag-innovation-logo.png" alt="Tag Innovation Logo" className="h-6 pl-1 w-auto" />
                <span className="ml-2 text-sm font-semibold">Tag-Inn</span>
              </Link>
            )
          )}
          {/* Navigation Links - Desktop only */}
          <nav className="hidden sm:flex items-center shrink-0">
            {!hideBusinessNavigation && (!user || location.pathname === '/' || location.pathname === '/features' || location.pathname === '/pricing') ? (
              <>
                {/* Features link hidden for now - not fully developed
                <NavLinkWithExpand
                  section="features"
                  expandedSection={expandedSection}
                  onMouseEnter={handleSectionMouseEnter}
                  onMouseLeave={handleSectionMouseLeave}
                  onClick={() => navigate('/features')}
                  isActive={location.pathname === '/features'}
                >
                  Features
                </NavLinkWithExpand>
                */}
                <SimpleNavLink to="/pricing">Pricing</SimpleNavLink>
              </>
            ) : !hideBusinessNavigation ? (
              <>
                {!isEmployee && (
                  <ButtonGroup aria-label="Dashboard navigation">
                    <Button
                      variant={location.pathname === '/dashboard' ? 'default' : 'outline'}
                      size="sm"
                      className="h-8 px-2 min-[900px]:px-3 text-xs"
                      onClick={() => navigate('/dashboard')}
                    >
                      <Star className="h-3.5 w-3.5 min-[900px]:mr-1.5" />
                      <span className="hidden min-[900px]:inline">Customers</span>
                    </Button>
                    <Button
                      variant={location.pathname === '/employee-dashboard' ? 'default' : 'outline'}
                      size="sm"
                      className="h-8 px-2 min-[900px]:px-3 text-xs"
                      onClick={() => navigate('/employee-dashboard')}
                    >
                      <Users className="h-3.5 w-3.5 min-[900px]:mr-1.5" />
                      <span className="hidden min-[900px]:inline">Team</span>
                    </Button>
                    {businesses.length >= 2 && (
                      <Button
                        variant={location.pathname === '/portfolio' ? 'default' : 'outline'}
                        size="sm"
                        className="h-8 px-2 min-[900px]:px-3 text-xs"
                        onClick={() => navigate('/portfolio')}
                      >
                        <Building2 className="h-3.5 w-3.5 min-[900px]:mr-1.5" />
                        <span className="hidden min-[900px]:inline">Portfolio</span>
                      </Button>
                    )}
                  </ButtonGroup>
                )}
                {isEmployee && (
                  <Button
                    variant={location.pathname === '/employee-portal' ? 'default' : 'outline'}
                    size="sm"
                    className="h-8 px-2 min-[900px]:px-3 text-xs"
                    onClick={() => navigate('/employee-portal')}
                  >
                    <UserCircle className="h-3.5 w-3.5 min-[900px]:mr-1.5" />
                    <span className="hidden min-[900px]:inline">My Portal</span>
                  </Button>
                )}
              </>
            ) : null}
          </nav>
        </div>

        {/* Center section */}
        {isMobile ? (
          /* Mobile: Business selector centered */
          user && showBusinessSelect ? (
            <div className="flex-1 flex justify-center">
              <Select
                value={selectedBusinessId || ""}
                onValueChange={handleBusinessChange}
                disabled={loadingBusinesses}
              >
                <SelectTrigger className="w-auto max-w-[220px] h-8 px-3 text-xs [&>span]:truncate">
                  {(() => {
                    const selectedBusiness = businesses.find(b => b.id === selectedBusinessId);
                    return selectedBusiness?.favicon_url ? (
                      <div className="h-5 w-5 mr-1.5 shrink-0 rounded-full bg-white flex items-center justify-center ring-1 ring-border">
                        <img
                          src={selectedBusiness.favicon_url}
                          alt=""
                          className="h-3.5 w-3.5 rounded-sm"
                          onError={(e) => {
                            const container = e.currentTarget.parentElement;
                            if (container) container.style.display = 'none';
                            container?.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      </div>
                    ) : null;
                  })()}
                  <Building2 className={`h-3.5 w-3.5 mr-1.5 shrink-0 ${businesses.find(b => b.id === selectedBusinessId)?.favicon_url ? 'hidden' : ''}`} />
                  <SelectValue placeholder="Select Business" />
                </SelectTrigger>
                <SelectContent>
                  {businesses.map((business) => (
                    <SelectItem key={business.id} value={business.id} hideIndicator>
                      {business.name}
                    </SelectItem>
                  ))}
                  <SelectSeparator />
                  <SelectItem value="manage-businesses" className="text-primary font-medium" hideIndicator>
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      <span>Manage</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : !user ? (
            <Link to="/" className="flex-1 flex justify-center items-center">
              <img src="/media/tag-innovation-logo.png" alt="Tag Innovation Logo" className="h-6 w-auto" />
              <span className="ml-2 text-sm font-semibold">Tag-Inn</span>
            </Link>
          ) : null
        ) : null}

        {/* Right section */}
        <div className={`flex items-center ${isMobile ? 'flex-1 justify-end' : 'gap-2'}`}>
          {!isMobile && (
            <>
              {user ? (
                <>
                  {location.pathname === '/create-business' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 border border-input"
                      onClick={handleLogout}
                      title="Logout"
                    >
                      <LogOut className="h-4 w-4" />
                      <span className="sr-only">Logout</span>
                    </Button>
                  )}
                  {!isOnDashboardPage && !hideBusinessNavigation && (
                    <Button variant="default" size="sm" asChild>
                      <Link to={isEmployee ? "/employee-portal" : "/dashboard"}>
                        Dashboard
                      </Link>
                    </Button>
                  )}
                  {showBusinessSettingsButton && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-8 w-8 border border-input ${isBusinessSettingsActive() ? 'bg-accent text-accent-foreground' : ''}`}
                      onClick={handleBusinessSettingsClick}
                      title="Business Settings"
                    >
                      <Settings className="h-4 w-4" />
                      <span className="sr-only">Business Settings</span>
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-8 w-8 border border-input ${isAccountSettingsActive() ? 'bg-accent text-accent-foreground' : ''}`}
                    onClick={handleAccountSettingsClick}
                    title="Account Settings"
                  >
                    <User className="h-4 w-4" />
                    <span className="sr-only">Account Settings</span>
                  </Button>
                </>
              ) : (
                  <>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/sign-in">Log in</Link>
                    </Button>
                    <Button size="sm" asChild>
                      <Link to="/sign-up">Sign up</Link>
                    </Button>
                  </>
                )}
              
              {user && <NotificationCenter />}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8">
                    <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                    <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                    <span className="sr-only">Toggle theme</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setTheme('light')} className={theme === 'light' ? 'bg-primary/15 dark:bg-primary/25' : ''}>Light</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme('dark')} className={theme === 'dark' ? 'bg-primary/15 dark:bg-primary/25' : ''}>Dark</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme('system')} className={theme === 'system' ? 'bg-primary/15 dark:bg-primary/25' : ''}>System</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}

          {isMobile && (
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8">
                    <Menu className="h-4 w-4" />
                    <span className="sr-only">Open menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[250px] sm:w-[300px]">
                  <div className="flex flex-col items-start space-y-4 pt-8">
                    {user ? (
                      <>
                        {showBusinessSettingsButton && (
                          <Button
                            variant="ghost"
                            className={`w-full text-left text-lg font-semibold p-0 h-auto justify-start ${isBusinessSettingsActive() ? 'text-primary' : 'text-foreground'}`}
                            onClick={handleBusinessSettingsClick}
                          >
                            <Settings className="h-5 w-5 mr-2" />
                            Business Settings
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          className={`w-full text-left text-lg font-semibold p-0 h-auto justify-start ${isAccountSettingsActive() ? 'text-primary' : 'text-foreground'}`}
                          onClick={handleAccountSettingsClick}
                        >
                          <User className="h-5 w-5 mr-2" />
                          {user.email}
                        </Button>
                        <Button variant="outline" onClick={handleLogout} className="w-full">Logout</Button>

                        {/* Theme Toggle */}
                        <div className="w-full border-t pt-4 mt-2">
                          <p className="text-sm text-muted-foreground mb-3">Theme</p>
                          <div className="flex gap-2">
                            <Button
                              variant={theme === 'light' ? 'default' : 'outline'}
                              size="sm"
                              className="flex-1"
                              onClick={() => setTheme('light')}
                            >
                              <Sun className="h-4 w-4 mr-1" />
                              Light
                            </Button>
                            <Button
                              variant={theme === 'dark' ? 'default' : 'outline'}
                              size="sm"
                              className="flex-1"
                              onClick={() => setTheme('dark')}
                            >
                              <Moon className="h-4 w-4 mr-1" />
                              Dark
                            </Button>
                          </div>
                          <Button
                            variant={theme === 'system' ? 'default' : 'outline'}
                            size="sm"
                            className="w-full mt-2"
                            onClick={() => setTheme('system')}
                          >
                            System
                          </Button>
                        </div>
                      </>
                    ) : !hideBusinessNavigation ? (
                      <>
                        {/* Features link hidden for now - not fully developed
                        <MobileNavLink to="/features">Features</MobileNavLink>
                        */}
                        <MobileNavLink to="/pricing">Pricing</MobileNavLink>
                        <div className="w-full pt-4">
                          <Button variant="outline" className="w-full mb-2" asChild><Link to="/sign-in">Log in</Link></Button>
                          <Button className="w-full" asChild><Link to="/sign-up">Sign up</Link></Button>
                        </div>

                        {/* Theme Toggle for non-logged in users */}
                        <div className="w-full border-t pt-4 mt-2">
                          <p className="text-sm text-muted-foreground mb-3">Theme</p>
                          <div className="flex gap-2">
                            <Button
                              variant={theme === 'light' ? 'default' : 'outline'}
                              size="sm"
                              className="flex-1"
                              onClick={() => setTheme('light')}
                            >
                              <Sun className="h-4 w-4 mr-1" />
                              Light
                            </Button>
                            <Button
                              variant={theme === 'dark' ? 'default' : 'outline'}
                              size="sm"
                              className="flex-1"
                              onClick={() => setTheme('dark')}
                            >
                              <Moon className="h-4 w-4 mr-1" />
                              Dark
                            </Button>
                          </div>
                          <Button
                            variant={theme === 'system' ? 'default' : 'outline'}
                            size="sm"
                            className="w-full mt-2"
                            onClick={() => setTheme('system')}
                          >
                            System
                          </Button>
                        </div>
                      </>
                    ) : null}
                  </div>
                </SheetContent>
              </Sheet>
          )}
        </div>
      </div>

      {/* Expanding Panel - Overlays on top of content */}
      <div
        className={`hidden sm:block absolute left-0 right-0 top-full bg-background shadow-lg border-b border-border overflow-hidden ${
          expandedSection
            ? 'max-h-96 opacity-100 translate-y-0 pointer-events-auto'
            : 'max-h-0 opacity-0 -translate-y-0 pointer-events-none'
        }`}
        style={{
          transition: 'max-height 350ms cubic-bezier(0.4, 0, 0.2, 1), opacity 350ms cubic-bezier(0.4, 0, 0.2, 1), transform 350ms cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: expandedSection ? 'max-height, opacity, transform' : 'auto',
        }}
        onMouseEnter={() => {
          // Only trigger if there's an actual section (prevent interference during closing)
          if (expandedSection) {
            handleSectionMouseEnter(expandedSection);
          }
        }}
        onMouseLeave={handleSectionMouseLeave}
      >
        {expandedSection === 'features' && <FeaturesExpandedContent navigate={navigate} />}
        {expandedSection === 'pricing' && <PricingExpandedContent navigate={navigate} />}
      </div>
    </nav>
    </>
  );
};

// NavLink with expand functionality
interface NavLinkWithExpandProps {
  section: ExpandedSection;
  expandedSection: ExpandedSection;
  onMouseEnter: (section: ExpandedSection) => void;
  onMouseLeave: () => void;
  onClick: () => void;
  isActive: boolean;
  children: React.ReactNode;
}

const NavLinkWithExpand: React.FC<NavLinkWithExpandProps> = ({
  section,
  expandedSection,
  onMouseEnter,
  onMouseLeave,
  onClick,
  isActive,
  children,
}) => {
  const linkClasses = `inline-flex items-center gap-1 px-1 pt-1 border-b-2 text-sm font-medium transition-colors cursor-pointer ${
    isActive ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
  }`;

  const isExpanded = expandedSection === section;

  return (
    <span
      className={linkClasses}
      onMouseEnter={() => onMouseEnter(section)}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {children}
      <ChevronDown
        className={`h-4 w-4 transition-transform duration-300 ${
          isExpanded ? 'rotate-180' : 'rotate-0'
        }`}
      />
    </span>
  );
};

// Simple NavLink without expand
const SimpleNavLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  const linkClasses = `inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors ${
    isActive ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
  }`;

  return (
    <Link to={to} className={linkClasses}>
      {children}
    </Link>
  );
};

// Features expanded content
const FeaturesExpandedContent = ({ navigate }: { navigate: (path: string) => void }) => {
  const featureSections = [
    {
      title: "CUSTOMER ENGAGEMENT",
      features: [
        { name: "Reviews & Feedback", icon: Star, link: "/features/reviews" },
        { name: "Loyalty & Rewards", icon: Heart, link: "/features/loyalty" },
        { name: "Marketing & Email", icon: Mail, link: "/features/marketing" },
      ]
    },
    {
      title: "WORKFORCE",
      features: [
        { name: "Employee Management", icon: Users, link: "/features/workforce" },
        { name: "Recruitment & Hiring", icon: Briefcase, link: "/features/recruitment" },
      ]
    },
    {
      title: "BUSINESS INTELLIGENCE",
      features: [
        { name: "Analytics & Insights", icon: BarChart3, link: "/features/analytics" },
        { name: "CRM & Customer Data", icon: Database, link: "/features/crm" },
      ]
    }
  ];

  return (
    <div className="bg-background">
      <div className="max-w-7xl mx-auto px-8 py-10">
        <div className="grid grid-cols-4 gap-12">
          {/* Feature columns */}
          {featureSections.map((section) => (
            <div key={section.title}>
              <h3 className="text-xs font-bold text-primary uppercase tracking-wider mb-6">
                {section.title}
              </h3>
              <div className="space-y-4">
                {section.features.map((feature) => {
                  const Icon = feature.icon;
                  return (
                    <Link
                      key={feature.name}
                      to={feature.link}
                      className="group flex items-center gap-3 transition-colors"
                    >
                      <Icon className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
                      <span className="text-sm text-foreground group-hover:text-primary transition-colors">
                        {feature.name}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Special section - Latest Updates */}
          <div>
            <h3 className="text-xs font-bold text-primary uppercase tracking-wider mb-6">
              LATEST UPDATES
            </h3>
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground leading-relaxed">
                Discover what's new in Tag-Inn
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span className="text-sm text-foreground">Enhanced pass generation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span className="text-sm text-foreground">Advanced analytics dashboard</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span className="text-sm text-foreground">Mobile-optimized experience</span>
                </li>
              </ul>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => navigate('/features')}
              >
                View All Features
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Pricing expanded content
const PricingExpandedContent = ({ navigate }: { navigate: (path: string) => void }) => {
  return (
    <div className="bg-background">
      <div className="max-w-3xl mx-auto px-8 py-8">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">Simple, Transparent Pricing</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Our review features are completely free, with additional premium features coming soon.
              No credit card required to get started - start collecting customer reviews today.
            </p>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate('/pricing')}
            className="ml-6"
          >
            View Pricing
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const MobileNavLink = ({ to, scrollTo, children }: { to?: string, scrollTo?: string, children: React.ReactNode }) => {
  const location = useLocation();
  const isActive = to ? location.pathname === to : false;

  if (scrollTo) {
    return (
      <span
        onClick={() => document.getElementById(scrollTo)?.scrollIntoView({ behavior: 'smooth' })}
        className="w-full text-lg font-semibold transition-colors text-foreground hover:text-primary cursor-pointer"
      >
        {children}
      </span>
    );
  }

  return (
    <Link
      to={to!}
      className={`w-full text-lg font-semibold transition-colors ${isActive ? 'text-primary' : 'text-foreground hover:text-primary'}`}>
      {children}
    </Link>
  );
}

export default Navbar;